import { z } from "zod";
import { staffCatalogItemStatuses, staffCatalogItemTypes, staffCatalogPriceUnits, staffCatalogTagNamespaces, } from "./staff-catalog.js";
// The contract of the staff catalog import (CHT-707): the routes W1 to W5, P1 and R1 to R4 under
// `/imports/catalog`. The API validates every body and query with these schemas, the admin and the typed
// client validate with the same ones, and every response is parsed with them before it is served or
// used. Every top-level shape is STRICT.
//
// The server is the parsing authority. The browser uploads the file to private storage, then asks the
// server to read it (P1) and to save a draft from it (W2). The browser never sends parsed rows.
//
// This package cannot import the domain or the import-mapping package. The code tuples below repeat
// theirs, and `apps/api` and `packages/application` have parity tests that fail when one differs.
//
// Dates travel as ISO 8601 strings. A response date is accepted as a `Date` or an ISO string and is
// always a `Date` in the output (the same rule as `staff-catalog.ts`).
// --- codes (parity-tested) -------------------------------------------------------------------------
export const staffImportKinds = ["CATALOG_ITEMS"];
export const staffImportMediaTypes = ["CSV", "TSV", "XLS", "XLSX"];
export const staffImportExtensions = ["csv", "tsv", "xls", "xlsx"];
export const staffImportJobStatuses = [
    "UPLOADED",
    "MAPPING",
    "VALIDATING",
    "REVIEW_REQUIRED",
    "APPROVED",
    "PROCESSING",
    "COMPLETED",
    "FAILED",
];
export const staffImportFailureCodes = [
    "FILE_UNREADABLE",
    "LIMIT_EXCEEDED",
    "VALIDATION_ABORTED",
    "PROCESSING_ERROR",
    "REJECTED_BY_STAFF",
];
export const staffImportRowOutcomes = [
    "READY",
    "WARNING",
    "INVALID",
    "DUPLICATE_CANDIDATE",
    "APPROVED",
    "IMPORTED",
    "SKIPPED",
];
export const staffImportEncodings = [
    "UTF8",
    "UTF8_BOM",
    "UTF16LE",
    "UTF16BE",
    "NOT_APPLICABLE",
];
export const staffImportDelimiters = ["COMMA", "SEMICOLON", "TAB", "NOT_APPLICABLE"];
export const staffImportColumnDecisionKinds = ["MAPPED", "IGNORED", "UNMAPPED"];
export const staffImportSuggestionConfidences = ["HIGH", "MEDIUM", "NONE"];
export const staffImportSuggestionSources = ["BUILT_IN", "LEARNED", "SIMILARITY", "NONE"];
export const staffImportDecisionOrigins = ["SUGGESTION", "STAFF"];
export const staffImportTargetGroups = [
    "common",
    "event",
    "dining",
    "villa",
    "experience",
    "service",
];
/**
 * Why the server refused a file. A refused file creates no job. `row` and `column` are 1-based and
 * point into the source sheet (a spreadsheet row number and column number); `null` when the code has
 * no place (a bad signature, an empty file). A message is rendered from the code: no cell text and no
 * file name is ever in an error.
 */
export const staffImportParseErrorCodes = [
    "UNSUPPORTED_FORMAT",
    "SIGNATURE_MISMATCH",
    "INVALID_ENCODING",
    "TOO_MANY_ROWS",
    "TOO_MANY_COLUMNS",
    "CELL_TOO_LONG",
    "ROW_TOO_LARGE",
    "ARCHIVE_LIMIT",
    "MALFORMED_FILE",
    "EMPTY_FILE",
    "SHEET_NOT_FOUND",
    "HEADER_ROW_NOT_FOUND",
    "FILE_TOO_LARGE",
];
/** What a column of the preview may carry as a note. */
export const staffImportColumnNotes = [
    "DUPLICATE_HEADER",
    "BLANK_HEADER",
    "FORMULA_CELLS",
    "DATE_FORMATTED",
    "EMPTY_COLUMN",
];
/** Why a mapping is refused (422). A message is rendered from the code. */
export const staffImportMappingIssueCodes = [
    "COLUMN_OUT_OF_RANGE",
    "COLUMN_DUPLICATE",
    "COLUMN_MISSING",
    "DECISION_SHAPE",
    "TARGET_UNKNOWN",
    "TARGET_DUPLICATE",
    "COLUMN_UNDECIDED",
    "NAME_NOT_MAPPED",
    "ITEM_TYPE_MISSING",
    "ITEM_TYPE_CONFLICT",
];
// --- bounds (parity-tested against the domain) --------------------------------------------------------
export const STAFF_IMPORT_BOUNDS = Object.freeze({
    fileName: 255,
    /** `IMPORT_MAX_BYTES` */
    maxBytes: 10_485_760,
    /** `IMPORT_MAX_ROWS` */
    maxRows: 5000,
    /** `IMPORT_MAX_CELLS` */
    maxColumns: 200,
    /** `IMPORT_MAX_CELL_CHARS` */
    maxCellChars: 2000,
    /** The preview shows this many rows of the sheet. */
    previewRows: 30,
    /** A cell in the preview is cut to this many characters. */
    previewCellChars: 200,
    /** Sample values of one column in the preview. */
    columnSamples: 3,
    sheetName: 31,
    sheets: 256,
    objectPath: 512,
    reason: 500,
    limit: 100,
    parseErrors: 20,
    mappingRevision: 500,
    /** The largest row number of a sheet. */
    sheetRow: 1_048_576,
    expiresInMinutes: 15,
});
// --- primitives -------------------------------------------------------------------------------------
const id = z.string().min(1).max(64);
const count = z.number().int();
const nullableText = z.string().nullable();
const responseInstant = z.union([
    z.date(),
    z.iso.datetime({ offset: true }).transform((value) => new Date(value)),
]);
const CURSOR = /^[A-Za-z0-9_-]+$/;
const SHA256 = /^[0-9a-f]{64}$/;
const TARGET_FIELD = /^[a-z][a-z0-9_]{0,63}$/;
/** `imports/<uploadId>.<extension>`: the key W1 hands out and nothing else. */
const OBJECT_PATH = /^imports\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(csv|tsv|xls|xlsx)$/;
const sha256Value = z.string().regex(SHA256);
const targetFieldValue = z.string().regex(TARGET_FIELD);
const objectPathValue = z.string().min(1).max(STAFF_IMPORT_BOUNDS.objectPath).regex(OBJECT_PATH);
const sheetIndexValue = z
    .number()
    .int()
    .min(0)
    .max(STAFF_IMPORT_BOUNDS.sheets - 1);
const headerRowValue = z.number().int().min(1).max(STAFF_IMPORT_BOUNDS.sheetRow);
const columnIndexValue = z
    .number()
    .int()
    .min(0)
    .max(STAFF_IMPORT_BOUNDS.maxColumns - 1);
const versionValue = z.number().int().positive();
const revisionValue = z.number().int().min(1).max(STAFF_IMPORT_BOUNDS.mappingRevision);
const itemTypeValue = z.enum(staffCatalogItemTypes);
// --- paths ------------------------------------------------------------------------------------------
export const STAFF_IMPORT_PATHS = Object.freeze({
    uploads: "/imports/catalog/uploads",
    previews: "/imports/catalog/previews",
    jobs: "/imports/catalog/jobs",
    aliases: "/imports/catalog/aliases",
});
/** What follows `/imports/catalog/jobs/:jobId/`. */
export const STAFF_IMPORT_JOB_SUBPATHS = Object.freeze({
    mapping: "mapping",
    cancel: "cancel",
    submit: "submit",
    rows: "rows",
    // Phase 3.4 (CHT-708): review, approval, processing and retry.
    reviewRows: "review-rows",
    progress: "progress",
    review: "review",
    bulkReview: "bulk-review",
    approve: "approve",
    retry: "retry",
});
/** The path of a job, or of one of its sub-resources. The id is encoded, so it cannot add a path segment. */
export function staffImportJobPath(jobId, subpath) {
    const base = `${STAFF_IMPORT_PATHS.jobs}/${encodeURIComponent(jobId)}`;
    return subpath === undefined ? base : `${base}/${subpath}`;
}
/** The same paths as Hono route templates (`:jobId`), for the API to register. */
export function staffImportJobRoute(subpath) {
    const base = `${STAFF_IMPORT_PATHS.jobs}/:jobId`;
    return subpath === undefined ? base : `${base}/${subpath}`;
}
/** R6 and W6: one row of a job, and its decisions. The ids are encoded, so they cannot add a path segment. */
export function staffImportRowPath(jobId, rowId, decisions = false) {
    const base = `${staffImportJobPath(jobId, "rows")}/${encodeURIComponent(rowId)}`;
    return decisions ? `${base}/decisions` : base;
}
export function staffImportRowRoute(decisions = false) {
    const base = `${staffImportJobRoute("rows")}/:rowId`;
    return decisions ? `${base}/decisions` : base;
}
// --- queries (R1, R3, R4) -----------------------------------------------------------------------------
const limitQuery = z
    .string()
    .regex(/^\d{1,3}$/)
    .transform(Number)
    .pipe(z.number().int().min(1).max(STAFF_IMPORT_BOUNDS.limit));
/** R1. Keyset page, newest first. There is no total: the browser pages with `cursor`. */
export const staffImportJobListQuerySchema = z.strictObject({
    status: z.enum(staffImportJobStatuses).optional(),
    limit: limitQuery.optional(),
    cursor: z.string().min(1).max(512).regex(CURSOR).optional(),
});
/** R3. Rows after `afterRowNumber` (exclusive), by row number. */
export const staffImportRowListQuerySchema = z.strictObject({
    limit: limitQuery.optional(),
    afterRowNumber: z
        .string()
        .regex(/^\d{1,4}$/)
        .transform(Number)
        .pipe(z.number().int().min(0).max(STAFF_IMPORT_BOUNDS.maxRows))
        .optional(),
});
export const staffImportAliasQuerySchema = z.strictObject({
    importKind: z.enum(staffImportKinds).optional(),
});
// --- request bodies ---------------------------------------------------------------------------------
/**
 * One column decision as staff send it. Only the intent travels: the server works out the confidence,
 * the suggestion source and the origin itself. `MAPPED` needs a `targetField`; `IGNORED` and
 * `UNMAPPED` must not have one. `remember` is kept only for a decision that staff made (not for a
 * suggestion that staff accepted unchanged).
 */
export const staffImportDecisionInputSchema = z
    .strictObject({
    columnIndex: columnIndexValue,
    decision: z.enum(staffImportColumnDecisionKinds),
    targetField: targetFieldValue.nullish(),
    remember: z.boolean().optional(),
})
    .refine((decision) => decision.decision === "MAPPED"
    ? decision.targetField != null
    : decision.targetField == null && decision.remember !== true, { message: "a mapped column needs a target; a column that is not mapped has none" });
const decisionList = z
    .array(staffImportDecisionInputSchema)
    .max(STAFF_IMPORT_BOUNDS.maxColumns)
    .refine((decisions) => new Set(decisions.map((decision) => decision.columnIndex)).size === decisions.length, { message: "a column has more than one decision" });
/** W1. Nothing is stored. The server refuses a bad extension or a size outside 1..10 MiB before any upload. */
export const staffImportUploadTargetBodySchema = z.strictObject({
    fileName: z.string().min(1).max(STAFF_IMPORT_BOUNDS.fileName),
    byteSize: z.number().int().min(1).max(STAFF_IMPORT_BOUNDS.maxBytes),
    extension: z.enum(staffImportExtensions),
});
/**
 * P1. Read-only: the server reads the stored file and answers what it found. `sheetIndex` and
 * `headerRowNumber` are optional: without them the server takes the first sheet and guesses the header
 * row (the first row with at least two non-empty cells).
 */
export const staffImportPreviewBodySchema = z.strictObject({
    objectPath: objectPathValue,
    sheetIndex: sheetIndexValue.optional(),
    headerRowNumber: headerRowValue.optional(),
});
/**
 * W2. One transaction: the server reads the stored file again, checks its hash against
 * `expectedSha256` (the hash P1 returned), writes the job and its raw rows, starts mapping, and saves
 * revision 1. One stored file makes one job. The sheet and the header row are fixed for the job.
 */
export const staffImportCreateDraftBodySchema = z.strictObject({
    objectPath: objectPathValue,
    fileName: z.string().min(1).max(STAFF_IMPORT_BOUNDS.fileName),
    expectedSha256: sha256Value,
    sheetIndex: sheetIndexValue,
    headerRowNumber: headerRowValue,
    decisions: decisionList,
    defaultItemType: itemTypeValue.nullish(),
});
/** W3. The job must be in MAPPING. `expectedRevision` is the revision the staff member last read. */
export const staffImportSaveMappingBodySchema = z.strictObject({
    expectedRevision: revisionValue,
    decisions: decisionList,
    defaultItemType: itemTypeValue.nullish(),
});
/** W4. Cancel a draft. The job fails with `REJECTED_BY_STAFF`. */
export const staffImportCancelBodySchema = z.strictObject({
    expectedVersion: versionValue,
    reason: z.string().min(1).max(STAFF_IMPORT_BOUNDS.reason),
});
/**
 * What a staff member may set for the validation run (CHT-708). Every key is optional: the server uses
 * `catalog_import`, no default zone and `ISO_ONLY`. The model-assist mode is never set here: the server
 * composition decides it.
 */
export const staffImportValidationSettingsSchema = z.strictObject({
    sourceSystem: z
        .string()
        .regex(/^[a-z][a-z0-9_]{0,63}$/)
        .optional(),
    defaultTimeZone: z.string().min(1).max(64).nullish(),
    dateOrder: z.enum(["ISO_ONLY", "DMY", "MDY"]).optional(),
});
/**
 * W5. Submit the saved mapping. It must have no UNMAPPED column and map the name and the item type.
 * `validation` (CHT-708, optional) sets the run settings; the server writes the run and queues the
 * validation in the same transaction.
 */
export const staffImportSubmitBodySchema = z.strictObject({
    expectedVersion: versionValue,
    expectedRevision: revisionValue,
    validation: staffImportValidationSettingsSchema.optional(),
});
// --- responses: upload target (W1) ----------------------------------------------------------------------
/**
 * Where the browser sends the file. `objectPath` is the key to give to P1 and W2 after the upload. The
 * URL and the headers belong to one exact key and expire at `expiresAt`.
 */
export const staffImportUploadTargetSchema = z.strictObject({
    objectPath: z.string().min(1).max(STAFF_IMPORT_BOUNDS.objectPath),
    url: z.string().min(1),
    method: z.enum(["PUT", "POST"]),
    headers: z.record(z.string(), z.string()),
    expiresAt: responseInstant,
});
// --- responses: column decisions and mapping ----------------------------------------------------------
/** One saved decision, with what the server suggested at the time. */
export const staffImportColumnDecisionSchema = z.strictObject({
    columnIndex: columnIndexValue,
    decision: z.enum(staffImportColumnDecisionKinds),
    targetField: targetFieldValue.nullable(),
    confidence: z.enum(staffImportSuggestionConfidences),
    suggestionSource: z.enum(staffImportSuggestionSources),
    suggestedTargetField: targetFieldValue.nullable(),
    origin: z.enum(staffImportDecisionOrigins),
    remember: z.boolean(),
});
const aliasVersions = z.strictObject({
    builtin: z.number().int().min(1),
    learned: z.number().int().min(0),
});
/**
 * One saved mapping revision. A revision is never changed: every save makes the next one. The header
 * texts are untrusted cell text: show them as text, never log them.
 */
export const staffImportMappingSchema = z.strictObject({
    revision: revisionValue,
    sourceSheetIndex: sheetIndexValue,
    sourceSheetName: z.string().max(STAFF_IMPORT_BOUNDS.sheetName),
    headerRowNumber: headerRowValue,
    sourceHeaders: z.array(z.string()).max(STAFF_IMPORT_BOUNDS.maxColumns),
    columnDecisions: z.array(staffImportColumnDecisionSchema).max(STAFF_IMPORT_BOUNDS.maxColumns),
    sourceEncoding: z.enum(staffImportEncodings),
    sourceDelimiter: z.enum(staffImportDelimiters),
    defaultItemType: itemTypeValue.nullable(),
    /** XLSX only: the workbook counts dates from 1904. For the validation phase. */
    date1904: z.boolean(),
    /** XLSX only: the 0-based indexes of the date-formatted columns. For the validation phase. */
    dateColumns: z.array(columnIndexValue).max(STAFF_IMPORT_BOUNDS.maxColumns),
    blankRowsSkipped: count,
    leadingRowsSkipped: count,
    builtinAliasVersion: z.number().int().min(1),
    learnedAliasVersion: z.number().int().min(0),
    createdByStaffUserId: id,
    createdAt: responseInstant,
});
// --- responses: preview (P1) ----------------------------------------------------------------------------
export const staffImportParseErrorSchema = z.strictObject({
    code: z.enum(staffImportParseErrorCodes),
    row: z.number().int().min(1).nullable(),
    column: z.number().int().min(1).nullable(),
});
export const staffImportMappingIssueSchema = z.strictObject({
    code: z.enum(staffImportMappingIssueCodes),
    columnIndex: z.number().int().min(0).nullable(),
    targetField: nullableText,
});
/** One row of the first rows of the sheet. `sourceRowNumber` is the spreadsheet row number (1-based). Cells are cut to 200 characters. */
export const staffImportPreviewRowSchema = z.strictObject({
    sourceRowNumber: z.number().int().min(1),
    cells: z.array(z.string()).max(STAFF_IMPORT_BOUNDS.maxColumns),
    blank: z.boolean(),
});
export const staffImportPreviewColumnSchema = z.strictObject({
    index: columnIndexValue,
    /** The header text, or an empty string for a blank header. Untrusted text. */
    header: z.string(),
    samples: z.array(z.string()).max(STAFF_IMPORT_BOUNDS.columnSamples),
    notes: z.array(z.enum(staffImportColumnNotes)),
});
/**
 * What the server found in a stored file. `sha256` is the hash of the bytes the server read: send it
 * back as `expectedSha256` in W2. `rows` are the first rows of the chosen sheet (so staff can pick the
 * header row). `columns` and `suggestions` are worked out for `headerRowNumber`. `suggestions` holds one
 * decision per column: HIGH suggestions come `MAPPED` with origin `SUGGESTION`; MEDIUM suggestions come
 * `UNMAPPED` (they are shown, never applied); the rest come `UNMAPPED`.
 */
export const staffImportPreviewSchema = z.strictObject({
    sha256: sha256Value,
    mediaType: z.enum(staffImportMediaTypes),
    sheets: z
        .array(z.strictObject({ index: sheetIndexValue, name: z.string() }))
        .max(STAFF_IMPORT_BOUNDS.sheets),
    sheetIndex: sheetIndexValue,
    rows: z.array(staffImportPreviewRowSchema).max(STAFF_IMPORT_BOUNDS.previewRows),
    /** The data rows below the header row, blank rows not counted. */
    rowCount: count,
    columnCount: count,
    headerRowNumber: headerRowValue,
    /** `true` when the server guessed the header row (the body had none). */
    headerRowGuessed: z.boolean(),
    columns: z.array(staffImportPreviewColumnSchema).max(STAFF_IMPORT_BOUNDS.maxColumns),
    suggestions: z.array(staffImportColumnDecisionSchema).max(STAFF_IMPORT_BOUNDS.maxColumns),
    aliasVersions,
    sourceEncoding: z.enum(staffImportEncodings),
    sourceDelimiter: z.enum(staffImportDelimiters),
    date1904: z.boolean(),
    dateColumns: z.array(columnIndexValue).max(STAFF_IMPORT_BOUNDS.maxColumns),
    blankRowsSkipped: count,
    leadingRowsSkipped: count,
    formulaCellCount: count,
});
// --- responses: jobs and rows (W2 to W5, R1 to R3) -----------------------------------------------------
/**
 * An import job. `fileName` is untrusted display text. A job waits in `MAPPING` until it is submitted
 * and then in `VALIDATING` (a later phase validates it). Nothing here has reached the catalog.
 */
export const staffImportJobSchema = z.strictObject({
    id,
    importKind: z.enum(staffImportKinds),
    status: z.enum(staffImportJobStatuses),
    version: count,
    attemptNumber: count,
    fileName: z.string(),
    mediaType: z.enum(staffImportMediaTypes),
    byteSize: count,
    sourceSha256: sha256Value,
    rowCount: count,
    failureCode: z.enum(staffImportFailureCodes).nullable(),
    createdByStaffUserId: id,
    createdByName: nullableText,
    statusChangedAt: responseInstant,
    createdAt: responseInstant,
    updatedAt: responseInstant,
});
/** A page of jobs, newest first. `nextCursor` is opaque: pass it back as `cursor`. `null` at the end. */
export const staffImportJobPageSchema = z.strictObject({
    items: z.array(staffImportJobSchema),
    nextCursor: z.string().min(1).max(512).nullable(),
});
/** The answer of W2 to W5 and R2: the job and its latest mapping revision. Reopening a draft starts from this. */
export const staffImportJobDetailSchema = z.strictObject({
    job: staffImportJobSchema,
    mapping: staffImportMappingSchema.nullable(),
});
/**
 * The proposal of one row: the trimmed, normalized text of each mapped field, built from the raw cells
 * and the submitted mapping. `null` until the mapping is submitted. Never canonical.
 */
export const staffImportProposalSchema = z.strictObject({
    itemType: itemTypeValue.nullable(),
    fields: z.record(targetFieldValue, z.union([z.string(), z.array(z.string())])),
});
/** One row: the raw source beside the proposal. Raw cells are untrusted and immutable. */
export const staffImportRowSchema = z.strictObject({
    rowNumber: z.number().int().min(1),
    /** The spreadsheet row number the row came from. `null` for a row stored before this was kept. */
    sourceRowNumber: z.number().int().min(1).nullable(),
    rawCells: z.array(z.string()).max(STAFF_IMPORT_BOUNDS.maxColumns),
    proposal: staffImportProposalSchema.nullable(),
    outcome: z.enum(staffImportRowOutcomes).nullable(),
});
/** R3. `nextAfterRowNumber` is the value to pass as `afterRowNumber` for the next page; `null` at the end. */
export const staffImportRowPageSchema = z.strictObject({
    rows: z.array(staffImportRowSchema),
    nextAfterRowNumber: z.number().int().min(1).nullable(),
});
// --- responses: aliases (R4) --------------------------------------------------------------------------
export const staffImportAliasListSchema = z.strictObject({
    importKind: z.enum(staffImportKinds),
    versions: aliasVersions,
    builtIn: z.array(z.strictObject({ key: z.string(), targetField: targetFieldValue, ordinal: z.boolean() })),
    learned: z.array(z.strictObject({
        key: z.string(),
        targetField: targetFieldValue,
        registryVersion: z.number().int().min(1),
    })),
});
// =====================================================================================================
// CHT-708 (Phase 3.4): normalization, duplicate resolution, review, approval and processing.
// The routes R5 to R7 and W6 to W10 under `/imports/catalog/jobs/:jobId`. FROZEN by the freeze commit:
// later changes are additive only. The tuples below repeat the domain's
// (`packages/domain/src/catalog-import-review.ts`, `background-jobs.ts`); a parity test in `apps/api`
// fails when one differs.
//
// Plain words: after a mapping is submitted the server validates every row with fixed rules and looks for
// items that already exist. Staff then see each row with its issues and the decisions they still owe
// (R5, R6). A decision (W6) is a new record, never an edit of the proposal. Staff approve or skip rows
// (W7, W8), approve the job (W9), watch it process (R7) and retry a failed job (W10). An import creates
// DRAFT items only. It never publishes.
// =====================================================================================================
// --- codes (parity-tested) -------------------------------------------------------------------------
export const staffImportIssueSeverities = ["ERROR", "WARNING"];
export const staffImportIssueCodes = [
    "REQUIRED_FIELD_MISSING",
    "INVALID_FORMAT",
    "UNKNOWN_CODE",
    "OUT_OF_RANGE",
    "INVALID_TIME_ZONE",
    "INVALID_INTERVAL",
    "PRICING_INCONSISTENT",
    "DUPLICATE_IN_FILE",
    "POSSIBLE_DUPLICATE",
    "UNSUPPORTED_VALUE",
    "AMBIGUOUS_CURRENCY",
    "AMBIGUOUS_VALUE",
    "TIME_ZONE_INFERRED",
    "MODEL_ASSISTED",
    "DESTRUCTIVE_UPDATE",
    "PLACE_ID_INVALID",
    "MATCH_SIGNALS_DISAGREE",
    "PLACE_ID_HELD_BY_OTHER_ITEM",
    "PLACE_ID_CONFLICT",
];
export const staffImportMatchBases = [
    "EXTERNAL_PROVIDER_ID",
    "SOURCE_ID",
    "NAME_LOCATION",
    "FUZZY_CANDIDATE",
];
export const staffImportMatchStrengths = ["STRONG", "WEAK"];
export const staffImportApprovalActions = ["CREATE_NEW", "UPDATE_EXISTING"];
export const staffImportDecisionKinds = [
    "CURRENCY",
    "TIME_ZONE",
    "DATE_ORDER",
    "TAXONOMY",
    "DUPLICATE_ACTION",
    "DESTRUCTIVE_UPDATE",
];
export const staffImportDuplicateActions = ["LINK_AS_UPDATE", "CREATE_NEW", "SKIP"];
export const staffImportDateOrders = ["ISO_ONLY", "DMY", "MDY"];
export const staffImportAssistModes = ["OFF", "FAKE", "PROVIDER"];
export const staffImportBulkSelectors = [
    "READY_AND_RESOLVED",
    "STRONG_UNCHANGED",
    "INVALID",
];
export const staffImportReviewOutcomes = ["APPROVED", "SKIPPED"];
export const staffImportReviewRefusalCodes = [
    "ROW_NOT_FOUND",
    "ROW_STATE",
    "DECISION_MISSING",
    "INVALID_ROW",
    "CANDIDATE_UNKNOWN",
];
export const staffImportApprovalBlockerCodes = [
    "ROWS_UNDECIDED",
    "NO_APPROVED_ROWS",
    "DECISIONS_OPEN",
];
export const staffImportNormalizationRules = [
    "CLEANED",
    "PARSED",
    "DATE_PARTS_JOINED",
    "CODE_MATCH",
    "LABEL_MATCH",
    "SYNONYM",
    "MODEL_ASSISTED",
    "CURRENCY_CODE",
    "CURRENCY_SYMBOL",
    "CURRENCY_COLUMN",
    "SPLIT_LIST",
    "COUNTRY_LOOKUP",
    "URL_NORMALIZED",
    "DEFAULT",
];
export const staffImportFieldSources = [
    "CELL",
    "CELLS",
    "JOB_SETTING",
    "CITY_LOOKUP",
    "MODEL",
    "ITEM_TYPE_DEFAULT",
];
export const staffImportZoneSources = ["COLUMN", "JOB_DEFAULT", "CITY_LOOKUP"];
export const staffImportProposalPricingModels = [
    "UNKNOWN",
    "FIXED",
    "FROM",
    "RANGE",
    "QUOTE_REQUIRED",
];
export const staffImportCurrencyStates = ["EXPLICIT", "AMBIGUOUS", "NOT_NEEDED"];
export const staffImportProcessingResults = [
    "SUCCEEDED",
    "FAILED_RETRYABLE",
    "FAILED_FINAL",
];
export const staffImportProcessingErrorCodes = [
    "ITEM_CHANGED_SINCE_REVIEW",
    "DECISION_MISSING",
    "DESTRUCTIVE_NOT_CONFIRMED",
    "SOURCE_TAKEN",
    "TAXONOMY_MISSING",
    "ITEM_NOT_EDITABLE",
    "CONSTRAINT",
    "LOCK_TIMEOUT",
    "INTERNAL",
];
export const staffImportProcessingStates = [
    "WAITING",
    "RETRYING",
    "FAILED",
    "CREATED",
    "UPDATED",
    "SKIPPED",
];
export const staffImportBackgroundTaskIds = [
    "catalog-import.validate",
    "catalog-import.process",
    "catalog-item.enrich",
    "catalog-item.embed",
    "customer-memory.extract",
    "messaging.membership-sync",
    "messaging.system-notice",
    "messaging.staff-access-revoke",
    "messaging.staff-message-remove",
    "request.sla-check",
    "payment-request.expire",
    "payment-request.reconcile",
    "notification.deliver",
    "trip.reminder",
    "itinerary-item.weather-check",
    "notification.identity-revoke",
    "customer-privacy.export",
    "customer-privacy.delete",
    "messaging.human-only-handoff",
    "notification.receipt-check",
];
export const staffImportIntentStates = [
    "PENDING",
    "CLAIMED",
    "SUCCEEDED",
    "FAILED",
    "CANCELLED",
];
/** Bounds of the review contract (parity-tested against the domain). */
export const STAFF_IMPORT_REVIEW_BOUNDS = Object.freeze({
    /** `IMPORT_MAX_REVIEW_DECISIONS` */
    decisionsPerReview: 500,
    /** `IMPORT_MAX_DUPLICATE_CANDIDATES` */
    candidates: 5,
    /** `IMPORT_MAX_PEOPLE_PER_ITEM` */
    people: 20,
    /** The allowed codes listed for one decision. */
    options: 200,
    /** The changes listed for one candidate. */
    changes: 60,
    chosenCode: 64,
    proposalTags: 60,
});
// --- primitives ---------------------------------------------------------------------------------------
const chosenCodeValue = z.string().min(1).max(STAFF_IMPORT_REVIEW_BOUNDS.chosenCode);
const reviewRowLimit = STAFF_IMPORT_REVIEW_BOUNDS.decisionsPerReview;
const basisPoints = z.number().int().min(0).max(10_000);
const isoInstant = z.iso.datetime({ offset: true });
// --- queries (R5) ---------------------------------------------------------------------------------------
/**
 * R5. Rows of a job after `afterRowNumber` (exclusive), by row number. `needsDecision=true` keeps the
 * rows that owe an open decision. `processing` keeps one processing state.
 */
export const staffImportReviewRowListQuerySchema = z.strictObject({
    outcome: z.enum(staffImportRowOutcomes).optional(),
    needsDecision: z
        .enum(["true", "false"])
        .transform((value) => value === "true")
        .optional(),
    processing: z.enum(staffImportProcessingStates).optional(),
    limit: limitQuery.optional(),
    afterRowNumber: staffImportRowListQuerySchema.shape.afterRowNumber,
});
const none = (value) => value == null;
/** The fields each decision kind takes (the same rules as `ImportDecisionInput` in the domain). */
function decisionShapeIsValid(d) {
    switch (d.kind) {
        case "CURRENCY":
        case "TIME_ZONE":
            return (!none(d.chosenCode) &&
                none(d.fieldCode) &&
                none(d.chosenCatalogItemId) &&
                none(d.confirmedChangeSha256) &&
                none(d.assistId));
        case "DATE_ORDER":
            return ((d.chosenCode === "DMY" || d.chosenCode === "MDY") &&
                none(d.fieldCode) &&
                none(d.chosenCatalogItemId) &&
                none(d.confirmedChangeSha256) &&
                none(d.assistId));
        case "TAXONOMY":
            return (!none(d.fieldCode) &&
                !none(d.chosenCode) &&
                none(d.chosenCatalogItemId) &&
                none(d.confirmedChangeSha256));
        case "DUPLICATE_ACTION": {
            const action = staffImportDuplicateActions.includes(d.chosenCode);
            return (action &&
                none(d.fieldCode) &&
                (d.chosenCode === "LINK_AS_UPDATE") === !none(d.chosenCatalogItemId) &&
                none(d.confirmedChangeSha256) &&
                none(d.assistId));
        }
        case "DESTRUCTIVE_UPDATE":
            return (!none(d.chosenCatalogItemId) &&
                !none(d.confirmedChangeSha256) &&
                none(d.chosenCode) &&
                none(d.fieldCode) &&
                none(d.assistId));
    }
}
const decisionShapeMessage = { message: "the fields do not fit the decision kind" };
const decisionFields = {
    kind: z.enum(staffImportDecisionKinds),
    fieldCode: targetFieldValue.nullish(),
    chosenCode: chosenCodeValue.nullish(),
    chosenCatalogItemId: id.nullish(),
    confirmedChangeSha256: sha256Value.nullish(),
    assistId: id.nullish(),
};
/** One decision as staff make it (the nested part of W6). */
export const staffImportDecisionBodySchema = z
    .strictObject(decisionFields)
    .refine(decisionShapeIsValid, decisionShapeMessage);
/**
 * W6. Records one decision for one row. The job must be in REVIEW_REQUIRED and `expectedJobVersion`
 * must be its version. The latest decision for a `(kind, fieldCode)` wins; nothing is edited or deleted.
 */
export const staffImportRecordDecisionBodySchema = z
    .strictObject({ expectedJobVersion: versionValue, ...decisionFields })
    .refine(decisionShapeIsValid, decisionShapeMessage);
/**
 * W7. Approve or skip chosen rows (the explicit review; at most 500). All or nothing: when one row is
 * refused, no row changes and the 422 body lists the refusals. `approvalAction` is only for an
 * APPROVED duplicate candidate; a WEAK match needs a `DUPLICATE_ACTION` decision first, and the server
 * reads the item from it.
 */
export const staffImportReviewBodySchema = z.strictObject({
    expectedJobVersion: versionValue,
    decisions: z
        .array(z.strictObject({
        rowId: id,
        outcome: z.enum(staffImportReviewOutcomes),
        approvalAction: z.enum(staffImportApprovalActions).optional(),
    }))
        .min(1)
        .max(reviewRowLimit)
        .refine((decisions) => new Set(decisions.map((entry) => entry.rowId)).size === decisions.length, {
        message: "a row has more than one decision",
    }),
});
/**
 * W8. A bulk review: the server selects the rows. `INVALID` and `STRONG_UNCHANGED` only skip. A WEAK
 * match is never taken. Rows that do not qualify are left alone and counted, never refused.
 */
export const staffImportBulkReviewBodySchema = z
    .strictObject({
    expectedJobVersion: versionValue,
    select: z.enum(staffImportBulkSelectors),
    outcome: z.enum(staffImportReviewOutcomes),
})
    .refine((body) => body.select === "READY_AND_RESOLVED" || body.outcome === "SKIPPED", {
    message: "this selection can only be skipped",
});
/** W9. Approve the job (REVIEW_REQUIRED to APPROVED). The server queues the processing in the same transaction. */
export const staffImportApproveBodySchema = z.strictObject({ expectedVersion: versionValue });
/**
 * W10. Retry a FAILED job. A retry is a NEW job (attempt number + 1) on the same stored file. Rows that
 * were imported come back as unchanged matches and are skipped; staff decisions are carried over by
 * row fingerprint. The answer is the new job.
 */
export const staffImportRetryBodySchema = z.strictObject({ expectedVersion: versionValue });
// --- responses: the proposal (v2) ---------------------------------------------------------------------------
const fieldRuleSchema = z.strictObject({
    rule: z.enum(staffImportNormalizationRules),
    source: z.enum(staffImportFieldSources),
});
export const staffImportProposalScheduleSchema = z.strictObject({
    startsAt: isoInstant,
    endsAt: isoInstant.nullable(),
    timeZone: z.string().min(1).max(64),
    precision: z.enum(["EXACT", "DATE_ONLY"]),
    zoneSource: z.enum(staffImportZoneSources),
});
export const staffImportProposalPriceSchema = z.strictObject({
    model: z.enum(staffImportProposalPricingModels),
    currency: z
        .string()
        .regex(/^[A-Z]{3}$/)
        .nullable(),
    currencyState: z.enum(staffImportCurrencyStates),
    amountMinor: z.number().int().nonnegative().nullable(),
    minAmountMinor: z.number().int().nonnegative().nullable(),
    maxAmountMinor: z.number().int().nonnegative().nullable(),
    unit: z.enum(staffCatalogPriceUnits).nullable(),
    terms: nullableText,
});
/**
 * The proposal of a row after normalization (`proposalSchemaVersion` 2). Normalized values and the rule
 * that made each (`fieldRules`, by target-field code). Never raw cell text. Untrusted display text:
 * `name`, `locality`, `venue_name` and the person names come from the file.
 */
export const staffImportProposalV2Schema = z.strictObject({
    version: z.literal(2),
    normalizerVersion: z.number().int().min(1),
    itemType: itemTypeValue,
    name: z.string(),
    shortDescription: nullableText,
    description: nullableText,
    addressText: nullableText,
    locality: nullableText,
    region: nullableText,
    countryCode: z
        .string()
        .regex(/^[A-Z]{2}$/)
        .nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    timeZone: nullableText,
    externalUrl: nullableText,
    /** The Google Place ID of the row (CHT-1950). Absent in a proposal made before this field existed. */
    placeId: nullableText.optional(),
    category: nullableText,
    tags: z
        .array(z.strictObject({ namespace: z.enum(staffCatalogTagNamespaces), code: z.string() }))
        .max(STAFF_IMPORT_REVIEW_BOUNDS.proposalTags),
    price: staffImportProposalPriceSchema.nullable(),
    schedule: staffImportProposalScheduleSchema.nullable(),
    subtype: z.record(targetFieldValue, z.union([z.string(), z.number()])),
    people: z
        .array(z.strictObject({
        displayName: z.string(),
        slugKey: z.string(),
        position: z.number().int().min(0),
    }))
        .max(STAFF_IMPORT_REVIEW_BOUNDS.people),
    source: z.strictObject({
        system: z.string(),
        key: z.string(),
        keyDerived: z.boolean(),
        fingerprint: sha256Value,
    }),
    fieldRules: z.record(targetFieldValue, fieldRuleSchema),
});
// --- responses: review rows (R5, R6, W6) ---------------------------------------------------------------------
export const staffImportIssueSchema = z.strictObject({
    severity: z.enum(staffImportIssueSeverities),
    code: z.enum(staffImportIssueCodes),
    fieldCode: targetFieldValue.nullable(),
});
/** The processing of an approved or finished row. `null` for a row that staff have not approved or skipped. */
export const staffImportRowProcessingSchema = z.strictObject({
    state: z.enum(staffImportProcessingStates),
    attempts: count,
    errorCode: z.enum(staffImportProcessingErrorCodes).nullable(),
    catalogItemId: id.nullable(),
});
/** One row in the review table. `name` is untrusted display text from the file. */
export const staffImportReviewRowSchema = z.strictObject({
    id,
    rowNumber: z.number().int().min(1),
    sourceRowNumber: z.number().int().min(1).nullable(),
    outcome: z.enum(staffImportRowOutcomes).nullable(),
    name: nullableText,
    itemType: itemTypeValue.nullable(),
    issues: z.array(staffImportIssueSchema).max(60),
    matchBasis: z.enum(staffImportMatchBases).nullable(),
    matchStrength: z.enum(staffImportMatchStrengths).nullable(),
    candidateCount: z.number().int().min(0).max(STAFF_IMPORT_REVIEW_BOUNDS.candidates),
    approvalAction: z.enum(staffImportApprovalActions).nullable(),
    /** Decisions staff still owe this row. */
    openDecisionCount: count,
    needsDecision: z.boolean(),
    processing: staffImportRowProcessingSchema.nullable(),
});
export const staffImportReviewRowPageSchema = z.strictObject({
    rows: z.array(staffImportReviewRowSchema),
    nextAfterRowNumber: z.number().int().min(1).nullable(),
});
/** A decision staff owe, or made. `options` lists the codes staff may choose (currency, taxonomy); empty otherwise. */
export const staffImportRequiredDecisionSchema = z.strictObject({
    kind: z.enum(staffImportDecisionKinds),
    fieldCode: targetFieldValue.nullable(),
    resolved: z.boolean(),
    suggestedCode: nullableText,
    options: z
        .array(z.strictObject({ code: z.string(), label: z.string() }))
        .max(STAFF_IMPORT_REVIEW_BOUNDS.options),
});
export const staffImportChangeSchema = z.strictObject({
    fieldCode: targetFieldValue,
    /** Display text of the current value of the item, or `null` when it is empty. */
    current: nullableText,
    proposed: nullableText,
    /** Replaces a value that is not empty with a different one (or reorders people). Needs `DESTRUCTIVE_UPDATE`. */
    destructive: z.boolean(),
});
/** An existing item the row may match. `changeSha256` is what a `DESTRUCTIVE_UPDATE` decision confirms. */
export const staffImportCandidateSchema = z.strictObject({
    catalogItemId: id,
    name: z.string(),
    itemType: itemTypeValue,
    status: z.enum(staffCatalogItemStatuses),
    matchBasis: z.enum(staffImportMatchBases),
    strength: z.enum(staffImportMatchStrengths),
    rank: z.number().int().min(1).max(STAFF_IMPORT_REVIEW_BOUNDS.candidates),
    scoreBp: basisPoints.nullable(),
    itemVersionSeen: z.number().int().min(1),
    /** The item's version now. A difference from `itemVersionSeen` makes the processor refuse the update. */
    itemVersionNow: z.number().int().min(1),
    /** The update would change nothing. */
    unchanged: z.boolean(),
    destructive: z.boolean(),
    changeSha256: sha256Value.nullable(),
    changes: z.array(staffImportChangeSchema).max(STAFF_IMPORT_REVIEW_BOUNDS.changes),
});
export const staffImportRowDecisionSchema = z.strictObject({
    id,
    kind: z.enum(staffImportDecisionKinds),
    fieldCode: targetFieldValue.nullable(),
    chosenCode: nullableText,
    chosenCatalogItemId: id.nullable(),
    confirmedChangeSha256: sha256Value.nullable(),
    assistId: id.nullable(),
    /** The decision was carried over from an earlier attempt of the same file. */
    carriedFromDecisionId: id.nullable(),
    decidedByStaffUserId: id,
    decidedByName: nullableText,
    createdAt: responseInstant,
});
/** What the model answered for a taxonomy cell. The cell text is not stored here. `accepted`: confidence was high enough to use without a decision. */
export const staffImportRowAssistSchema = z.strictObject({
    id,
    fieldCode: targetFieldValue,
    choiceCode: nullableText,
    confidenceBp: basisPoints,
    modelId: z.string(),
    modelVersion: z.string(),
    promptVersion: z.string(),
    accepted: z.boolean(),
});
export const staffImportRowAttemptSchema = z.strictObject({
    attemptNumber: z.number().int().min(1).max(10),
    result: z.enum(staffImportProcessingResults),
    errorCode: z.enum(staffImportProcessingErrorCodes).nullable(),
    catalogItemId: id.nullable(),
    createdAt: responseInstant,
});
/**
 * R6 and the answer of W6: one row with everything the review drawer shows. `headers` are the mapping's
 * source headers (so `rawCells` can be shown under them). Raw cells and headers are untrusted text.
 * `proposal` is `null` until the row is validated.
 */
export const staffImportReviewRowDetailSchema = z.strictObject({
    row: staffImportReviewRowSchema,
    headers: z.array(z.string()).max(STAFF_IMPORT_BOUNDS.maxColumns),
    rawCells: z.array(z.string()).max(STAFF_IMPORT_BOUNDS.maxColumns),
    proposal: staffImportProposalV2Schema.nullable(),
    requiredDecisions: z.array(staffImportRequiredDecisionSchema).max(60),
    candidates: z.array(staffImportCandidateSchema).max(STAFF_IMPORT_REVIEW_BOUNDS.candidates),
    decisions: z.array(staffImportRowDecisionSchema).max(200),
    assists: z.array(staffImportRowAssistSchema).max(60),
    attempts: z.array(staffImportRowAttemptSchema).max(10),
});
// --- responses: reviews, progress (W7, W8, R7) --------------------------------------------------------------
export const staffImportReviewRefusalSchema = z.strictObject({
    rowId: id,
    code: z.enum(staffImportReviewRefusalCodes),
});
export const staffImportApprovalBlockerSchema = z.strictObject({
    code: z.enum(staffImportApprovalBlockerCodes),
    count: count,
});
/** The answer of W7 and W8. `job` carries the new version. `notEligibleCount` is for W8 (rows the selection did not take). */
export const staffImportReviewResultSchema = z.strictObject({
    job: staffImportJobSchema,
    approvedCount: count,
    skippedCount: count,
    notEligibleCount: count,
});
/**
 * R7. Where a job stands. `counts` are rows by outcome (`pending` rows have none). `processing` counts
 * rows by what happened to them after approval. For a finished job:
 * `rowCount = invalid + skipped + created + updated + failed` (and `waiting` and `retrying` are 0).
 */
export const staffImportProgressSchema = z.strictObject({
    job: staffImportJobSchema,
    counts: z.strictObject({
        pending: count,
        ready: count,
        warning: count,
        invalid: count,
        duplicate: count,
        approved: count,
        imported: count,
        skipped: count,
    }),
    processing: z.strictObject({
        waiting: count,
        retrying: count,
        failed: count,
        created: count,
        updated: count,
        skipped: count,
    }),
    openDecisionRows: count,
    openDecisions: count,
    /** `null` before the run is written (the job is not yet in VALIDATING). */
    validation: z
        .strictObject({
        normalizerVersion: z.number().int().min(1),
        sourceSystem: z.string(),
        defaultTimeZone: nullableText,
        dateOrder: z.enum(staffImportDateOrders),
        assistMode: z.enum(staffImportAssistModes),
        assistCalls: count,
        assistAccepted: count,
    })
        .nullable(),
    /** The background task of the job (validation, then processing). `null` when none was queued. */
    intent: z
        .strictObject({
        taskId: z.enum(staffImportBackgroundTaskIds),
        state: z.enum(staffImportIntentStates),
        attempts: count,
        maxAttempts: count,
        lastErrorCode: nullableText,
        nextAttemptAt: responseInstant.nullable(),
    })
        .nullable(),
    approval: z.strictObject({
        canApprove: z.boolean(),
        blockers: z.array(staffImportApprovalBlockerSchema).max(8),
    }),
});
// --- errors ---------------------------------------------------------------------------------------------
/**
 * The extra keys of a 422 body, beside the shared error envelope (`error`, `requestId`,
 * `correlationId`): `parseErrors` on P1 or W2 (the file was refused), `mappingIssues` on W2, W3 or W5
 * (the mapping was refused). The lists hold codes and positions only: no cell text, no header text, no
 * file name. The envelope itself is not changed.
 */
export const staffImportFailureDetailsSchema = z.object({
    parseErrors: z.array(staffImportParseErrorSchema).max(STAFF_IMPORT_BOUNDS.parseErrors).optional(),
    mappingIssues: z.array(staffImportMappingIssueSchema).max(500).optional(),
    /** W6 and W7 (CHT-708): which rows a review refused, by id and code. */
    reviewRefusals: z.array(staffImportReviewRefusalSchema).max(500).optional(),
    /** W9 (CHT-708): why the job cannot be approved. */
    approvalBlockers: z.array(staffImportApprovalBlockerSchema).max(8).optional(),
});
